import { action, observable } from "mobx";
import IsdsResponse from "models/isdsResponse";
import LicensePlateChange from "models/licensePlateChange";
import LicensePlateChangeRepository from "repositories/licensePlateChangeRepository";
import EshopError from "../models/eshopError";
import EventBus, {
  licensePlateChangeCreationFailed,
  licensePlateChangeDocumentsUploadFailed,
  licensePlateChangeDocumentsUploadSuccess,
  vehicleChangeApplicationProduction,
} from "./eventBus";
import { Notice } from "./NIA/niaService";
import { SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY } from "./omissionService";
import { PersistedBase64 } from "../models/notice";
import { plainToClass } from "class-transformer";
import * as Sentry from "@sentry/react";
import VehicleChangeApplicationProductionResponse from "../entities/vehicleChangeApplicationProductionResponse";
import ChargeRegistrationService from "./chargeRegistrationService";

export const POST_LICENSE_PLATE_CHANGE_CREATION_FAILED = "POST_LICENSE_PLATE_CHANGE_CREATION_FAILED";
export const POST_LICENSE_PLATE_CHANGE_DOCUMENTS_UPLOAD_FAILED = "POST_LICENSE_PLATE_CHANGE_DOCUMENTS_UPLOAD_FAILED";

export default class LicensePlateChangeService {
  @observable notice: LicensePlateChange = new LicensePlateChange();
  @observable vehicleChangeApplicationProduction: VehicleChangeApplicationProductionResponse | undefined;

  constructor(
    private repository: LicensePlateChangeRepository,
    private chargeRegistrationService: ChargeRegistrationService,
    private eventBus: EventBus
  ) {
    this.initialize();
  }

  @action.bound
  async createLicensePlateChangeNotice(licensePlateChange: LicensePlateChange) {
    return await this.repository
      .createLicensePlateChangeNotice(licensePlateChange)
      .then(async (response: any | EshopError) => {
        if (response instanceof EshopError) {
          this.eventBus.publish(licensePlateChangeCreationFailed({ type: response.type }));
        } else if (response.blob && response.fileName) {
          return response;
        }
      })
      .catch(() => {
        this.eventBus.publish(licensePlateChangeCreationFailed({ type: POST_LICENSE_PLATE_CHANGE_CREATION_FAILED }));
        return undefined;
      });
  }

  @action.bound
  async getProduction(notice: LicensePlateChange) {
    return await this.repository
      .getProduction(notice)
      .then(response => {
        this.vehicleChangeApplicationProduction = response as VehicleChangeApplicationProductionResponse;
        return response;
      })
      .catch(() => this.eventBus.publish(vehicleChangeApplicationProduction({ vehicleChangeApplicationProduction: undefined })));
  }

  @action.bound
  async uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    sessionStorage.setItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY, Notice.LICENSE_PLATE_CHANGE);
    return await this.repository
      .uploadDocuments(ISDSSessionId, files)
      .then(response => {
        if (response instanceof IsdsResponse) {
          this.eventBus.publish(licensePlateChangeDocumentsUploadSuccess({ conceptIdent: response.conceptIdent }));
        } else {
          this.publishDocumentsUploadFailure(response.type, response, files, ISDSSessionId);
        }
      })
      .catch(error => {
        this.publishDocumentsUploadFailure(POST_LICENSE_PLATE_CHANGE_DOCUMENTS_UPLOAD_FAILED, error, files, ISDSSessionId);
      });
  }

  private publishDocumentsUploadFailure(type: string, error: any, files: PersistedBase64[], sessionId: string) {
    Sentry.withScope(scope => {
      scope.setTag("LicensePlateService", "upload");
      scope.setExtras({
        type,
        files,
        sessionId,
      });
      Sentry.captureException(error);
    });

    this.eventBus.publish(licensePlateChangeDocumentsUploadFailed({ type }));
  }

  @action.bound
  initialize() {
    const noticeString = sessionStorage.getItem("license_plate_change");
    this.notice = new LicensePlateChange();

    if (noticeString) {
      this.notice = plainToClass(LicensePlateChange, JSON.parse(noticeString));
    }

    this.notice.vehicleOperator = undefined;
  }

  @action.bound
  reset = () => {
    this.notice = new LicensePlateChange();
    this.vehicleChangeApplicationProduction = undefined;
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
  };
}
